import {
  RoutePlanFeedbackChangeTagEnum,
  RoutePlanFeedbackMyAnswerDTO,
} from 'api/model';
import Button from 'components/inputs/Button';
import TextArea from 'components/inputs/TextArea';
import React, { useState } from 'react';
import styled from 'styled-components';
import StarRating from '../StarRating';
import {
  CHANGE_TAG_OPTIONS,
  FEEDBACK_QUESTION,
  showsChangeTags,
} from './feedback-round-helpers';

const Card = styled.form`
  background: ${({ theme }) => theme.colors.background.primary};
  border: 1px solid ${({ theme }) => theme.colors.border.primary};
  border-radius: 10px;
  padding: 16px;
  display: grid;
  gap: 16px;
  align-content: start;
`;

const Question = styled.fieldset`
  border: 0;
  margin: 0;
  padding: 0;
  display: grid;
  gap: 8px;
  min-width: 0;
`;

const Legend = styled.legend`
  font-weight: 600;
  padding: 0;
  margin-bottom: 8px;
`;

const Chips = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
`;

const Chip = styled.button<{ selected: boolean }>`
  border: 1px solid
    ${({ selected, theme }) =>
      selected
        ? theme.colors.background.selection
        : theme.colors.border.primary};
  background: ${({ selected, theme }) =>
    selected
      ? theme.colors.background.highlight
      : theme.colors.background.primary};
  color: ${({ theme }) => theme.colors.foreground.primary};
  border-radius: 999px;
  padding: 4px 11px;
  font: inherit;
  font-size: 0.86rem;
  cursor: pointer;
`;

const Label = styled.label`
  display: grid;
  gap: 6px;
  font-weight: 600;
`;

const CommentBox = styled(TextArea)`
  width: 100%;
  box-sizing: border-box;
  resize: vertical;
`;

const Optional = styled.span`
  font-weight: 400;
  color: ${({ theme }) => theme.colors.foreground.tertiary};
`;

const Actions = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  align-items: center;
`;

const SkipButton = styled.button`
  background: none;
  border: 0;
  color: ${({ theme }) => theme.colors.foreground.tertiary};
  font: inherit;
  cursor: pointer;
  text-decoration: underline;
`;

const ErrorText = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.colors.foreground.error};
`;

export interface AnswerValue {
  rating: number;
  changeTags: RoutePlanFeedbackChangeTagEnum[];
  comment: string;
}

interface Props {
  initial?: RoutePlanFeedbackMyAnswerDTO | null;
  preselectedRating?: number;
  sending: boolean;
  error?: string;
  onSubmit: (value: AnswerValue) => void;
  onSkip: () => void;
}

const AnswerForm: React.FC<Props> = ({
  initial,
  preselectedRating,
  sending,
  error,
  onSubmit,
  onSkip,
}) => {
  // Läses bara när formuläret monteras. Sidan monterar om det per paket (key), så en
  // bakgrundshämtning av paketet kan aldrig skriva över ett svar som håller på att skrivas.
  const [rating, setRating] = useState<number | undefined>(
    initial?.rating ?? preselectedRating
  );
  const [tags, setTags] = useState<RoutePlanFeedbackChangeTagEnum[]>(
    initial?.changeTags ?? []
  );
  const [comment, setComment] = useState(initial?.comment ?? '');

  const toggleTag = (tag: RoutePlanFeedbackChangeTagEnum) =>
    setTags((current) =>
      current.includes(tag)
        ? current.filter((t) => t !== tag)
        : [...current, tag]
    );

  const showTags = showsChangeTags(rating);

  return (
    <Card
      onSubmit={(e) => {
        e.preventDefault();
        if (rating === undefined) return;
        onSubmit({
          rating,
          changeTags: showTags ? tags : [],
          comment,
        });
      }}
    >
      <Question>
        <Legend>{FEEDBACK_QUESTION}</Legend>
        <StarRating value={rating} onChange={setRating} />
      </Question>

      {showTags && (
        <Question>
          <Legend>
            Vad hade du ändrat? <Optional>(välj gärna flera)</Optional>
          </Legend>
          <Chips>
            {CHANGE_TAG_OPTIONS.map((option) => (
              <Chip
                key={option.value}
                type="button"
                aria-pressed={tags.includes(option.value)}
                selected={tags.includes(option.value)}
                onClick={() => toggleTag(option.value)}
              >
                {option.label}
              </Chip>
            ))}
          </Chips>
        </Question>
      )}

      <Label htmlFor="route-plan-feedback-comment">
        <span>
          Med egna ord <Optional>(frivilligt)</Optional>
        </span>
        <CommentBox
          id="route-plan-feedback-comment"
          rows={4}
          value={comment}
          maxLength={4000}
          placeholder="T.ex. vad du hade gjort i stället"
          onChange={(e) => setComment(e.target.value)}
        />
      </Label>

      {error && <ErrorText>{error}</ErrorText>}

      <Actions>
        <Button type="submit" disabled={rating === undefined} loading={sending}>
          {initial ? 'Uppdatera svaret' : 'Skicka feedback'}
        </Button>
        <SkipButton type="button" onClick={onSkip}>
          Hoppa över
        </SkipButton>
      </Actions>
    </Card>
  );
};

export default AnswerForm;
